import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';
import { PLANS, PLAN_ORDER } from '../lib/plans';

const PricingTiersPicker = ({
  plans = PLAN_ORDER,
  context = 'project-creation',
  onSelect,
  disabled = false,
  title,
  subtitle,
  compact = false,
}) => {
  const [selectingPlan, setSelectingPlan] = useState(null);
  const [error, setError] = useState(null);

  const planIds = plans.filter((id) => PLANS[id]);

  const handleSelect = async (planId) => {
    if (disabled || selectingPlan) return;
    try {
      setError(null);
      setSelectingPlan(planId);
      await onSelect?.(planId, context);
    } catch (err) {
      console.error('Plan selection failed:', err);
      setError(err?.message || 'Could not start checkout. Please try again.');
      setSelectingPlan(null);
    }
  };

  return (
    <div className="w-full">
      {(title || subtitle) && (
        <div className="text-center mb-8">
          {title && (
            <h2 className="font-jersey text-3xl md:text-4xl text-black mb-2">{title}</h2>
          )}
          {subtitle && (
            <p className="text-black/60 text-base md:text-lg">{subtitle}</p>
          )}
        </div>
      )}

      <div
        className={`grid gap-4 md:gap-6 ${
          planIds.length === 1
            ? 'grid-cols-1 max-w-md mx-auto'
            : planIds.length === 2
              ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto'
              : 'grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto'
        }`}
      >
        {planIds.map((planId) => {
          const plan = PLANS[planId];
          const isHighlight = !!plan.highlight;
          const isLoading = selectingPlan === planId;

          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`relative rounded-2xl p-6 md:p-8 flex flex-col bg-white shadow-sm border transition-all ${
                isHighlight
                  ? 'border-vibe-cyan ring-2 ring-vibe-cyan/30 shadow-lg'
                  : 'border-black/10 hover:border-black/20 hover:shadow-md'
              }`}
            >
              {isHighlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-vibe-cyan text-black text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Most popular
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-xl font-semibold text-black">{plan.name}</h3>
                <p className="text-sm text-black/50 mt-1">{plan.tagline}</p>
              </div>

              <div className="mb-5 flex items-baseline">
                <span className="text-3xl md:text-4xl font-bold text-black">
                  {plan.amountLabel}
                </span>
                {plan.cadence && plan.cadence !== 'one-time' && (
                  <span className="text-sm text-black/50 ml-1">{plan.cadence}</span>
                )}
                {plan.cadence === 'one-time' && (
                  <span className="text-sm text-black/50 ml-2">one-time</span>
                )}
              </div>

              {!compact && (
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start text-sm text-black/70">
                      <Check className="w-4 h-4 text-vibe-cyan mt-0.5 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                onClick={() => handleSelect(plan.id)}
                disabled={disabled || isLoading}
                className={`w-full px-5 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isHighlight
                    ? 'bg-black text-white hover:bg-black/90'
                    : 'bg-white text-black border border-black/15 hover:bg-black/5'
                }`}
              >
                {isLoading ? (
                  <span className="inline-flex items-center justify-center">
                    <svg
                      className="animate-spin h-4 w-4 mr-2"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Redirecting…
                  </span>
                ) : (
                  plan.cta
                )}
              </button>
            </motion.div>
          );
        })}
      </div>

      {error && (
        <div className="max-w-md mx-auto mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <p className="text-center text-xs text-black/40 mt-6">
        🔒 Secure payment by Stripe. Cancel anytime on the Pro plan.
      </p>
    </div>
  );
};

export default PricingTiersPicker;
